import { useEffect, useSyncExternalStore } from 'react';

/**
 * "A checkout sheet is on screen right now."
 *
 * ## Why this exists
 *
 * `OnboardingModal` is mounted once, app-wide (`app-layout.tsx`), and opens
 * itself for a freshly signed-in user with steps left to do. Since guests
 * started getting an account out of the PAYMENT itself (`useGuestIdentify`,
 * 2026-09-19), "freshly signed in" now happens in the middle of a checkout: the
 * buyer types an address, presses «Оплатить», `/auth/start` creates the account
 * — and onboarding opened on top of the card sheet, before a single сум had
 * moved, hiding the form they were filling in (Igor, 2026-09-20).
 *
 * The two components share no tree and no state, and the sheets are rendered
 * per plan card deep inside /plans, so this is a module-level counter rather
 * than context: any sheet can raise it, any listener can read it, and no
 * provider has to wrap anything.
 *
 * ## A counter, not a boolean
 *
 * Two sheets are legitimately "open" at the same moment for a few frames — the
 * Payme body and the Octobank body swap on the gateway strip, and the incoming
 * one mounts before the outgoing one unmounts. A boolean would be cleared by
 * the departing sheet and let onboarding through in the gap; a counter cannot
 * go to zero until the last one has really gone.
 *
 * ## What this does NOT do
 *
 * It does not dismiss or retire anything. Onboarding simply does not OPEN while
 * the counter is above zero — and, crucially, does not spend one of its three
 * allowed showings either (`onboarding-gate.ts`). When the sheet closes the
 * counter returns to zero and onboarding opens on the next render exactly as it
 * always did.
 */
let openSheets = 0;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** A checkout sheet appeared. Pair with `exitCheckoutSheet`. */
export function enterCheckoutSheet(): void {
  openSheets += 1;
  emit();
}

/** A checkout sheet went away. Clamped at zero so an unbalanced call — a double
 *  unmount in StrictMode, a cleanup that ran twice — cannot drive it negative
 *  and latch "a sheet is open" forever. */
export function exitCheckoutSheet(): void {
  openSheets = Math.max(0, openSheets - 1);
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): boolean {
  return openSheets > 0;
}

/** Nothing is ever open during SSR/prerender — there is no user to open it. A
 *  distinct server snapshot is required, or React warns about a hydration
 *  mismatch on every page. */
function getServerSnapshot(): boolean {
  return false;
}

/** Read the flag. Re-renders the caller when it flips. */
export function useCheckoutSheetOpen(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Declare that THIS component is a checkout sheet while `open` is true.
 *
 * Counts on mount-open and un-counts on close or unmount, so a sheet that is
 * torn down outright (the guest layout unmounts its sheets on close) is as safe
 * as one that merely flips `open` to false.
 */
export function useCheckoutSheetPresence(open: boolean): void {
  useEffect(() => {
    if (!open) return;
    enterCheckoutSheet();
    return exitCheckoutSheet;
  }, [open]);
}

/** TEST ONLY — drop any leftover count between cases. */
export function resetCheckoutPresence(): void {
  openSheets = 0;
  emit();
}
